import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import ImageUploader from '@/components/feature/ImageUploader';
import ConfirmModal from '@/components/base/ConfirmModal';

async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

interface SettingsMap {
  [key: string]: string;
}

interface AdminRole {
  id: number;
  name: string;
  display_name: string;
  permissions: string[];
  created_at: string;
}

interface AdminUser {
  id: number;
  username: string;
  role: string;
  display_name: string;
  created_at: string;
}

interface Banner {
  id: number;
  title: string;
  subtitle: string;
  img_url: string;
  link_url: string;
  link_text: string;
  sort_order: number;
  is_active: boolean;
}

type Tab = 'general' | 'seo' | 'banners' | 'roles' | 'admins';

function BannerModal({ banner, onClose, onSaved }: { banner: Banner | null; onClose: () => void; onSaved: () => void }) {
  const isNew = !banner;
  const [form, setForm] = useState({
    title: banner?.title || '',
    subtitle: banner?.subtitle || '',
    img_url: banner?.img_url || '',
    link_url: banner?.link_url || '/contact',
    link_text: banner?.link_text || '立即预约咨询',
    sort_order: banner?.sort_order ?? 0,
    is_active: banner?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.title) { setErr('标题不能为空'); return; }
    if (!form.img_url) { setErr('请上传轮播图图片'); return; }
    setSaving(true);
    setErr('');
    const payload = {
      ...form,
      sort_order: parseInt(String(form.sort_order)) || 0,
      updated_at: new Date().toISOString(),
    };
    if (isNew) {
      const { error } = await supabase.from('banners').insert(payload);
      if (error) { setErr(error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('banners').update(payload).eq('id', banner!.id);
      if (error) { setErr(error.message); setSaving(false); return; }
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <h2 className="font-bold text-[#1a3a5c]">{isNew ? '添加轮播图' : '编辑轮播图'}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">轮播标题 *</label>
            <input type="text" value={form.title} onChange={set('title')} placeholder="如：吉尔吉斯斯坦 · 合法代孕 · 全球服务" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">副标题（可选）</label>
            <textarea value={form.subtitle} onChange={set('subtitle')} rows={2} placeholder="显示在首页主标题下方的描述文字" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] resize-none" />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">横幅大图 * <span className="text-gray-400">建议 1600×900px</span></label>
            <ImageUploader
              currentUrl={form.img_url}
              onChange={(url) => setForm((p) => ({ ...p, img_url: url }))}
              category="home"
              placeholder="点击上传横幅大图（JPG/PNG/WebP，最大2MB）"
              previewHeight="h-40"
              maxWidth={800}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">按钮链接</label>
              <input type="text" value={form.link_url} onChange={set('link_url')} placeholder="如 /contact" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">按钮文字</label>
              <input type="text" value={form.link_text} onChange={set('link_text')} placeholder="如 立即预约咨询" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">排序号</label>
              <input type="number" value={form.sort_order} onChange={set('sort_order')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-[#1a3a5c]">是否启用</label>
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, is_active: !p.is_active }))}
                className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${form.is_active ? 'bg-[#c9a96e]' : 'bg-gray-200'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${form.is_active ? 'left-5' : 'left-0.5'}`}></span>
              </button>
            </div>
          </div>

          {err && <p className="text-red-500 text-xs">{err}</p>}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer hover:border-gray-300 whitespace-nowrap">取消</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? '保存中...' : '保存轮播图'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddAdminModal({ onClose, onSaved, roles }: { onClose: () => void; onSaved: () => void; roles: AdminRole[] }) {
  const [form, setForm] = useState({ username: '', password: '', display_name: '', role: roles[0]?.name || 'editor' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.username || !form.password) { setErr('用户名和密码不能为空'); return; }
    setSaving(true);
    setErr('');
    const hashed = await sha256(form.password);
    const { error } = await supabase.from('cms_admins').insert({
      username: form.username,
      password_hash: hashed,
      display_name: form.display_name || form.username,
      role: form.role,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-[#1a3a5c]">添加管理员</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">用户名 *</label>
            <input type="text" value={form.username} onChange={set('username')} placeholder="登录用户名" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">密码 *</label>
            <input type="text" value={form.password} onChange={set('password')} placeholder="设置密码" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">显示名称</label>
            <input type="text" value={form.display_name} onChange={set('display_name')} placeholder="管理员显示名称" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">角色</label>
            <select value={form.role} onChange={set('role')} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white">
              {roles.map((r) => (
                <option key={r.id} value={r.name}>{r.display_name}</option>
              ))}
            </select>
          </div>
          {err && <p className="text-red-500 text-xs">{err}</p>}
          <button onClick={handleSave} disabled={saving} className="w-full bg-[#1a3a5c] hover:bg-[#0d2137] text-white font-semibold py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
            {saving ? '创建中...' : '创建管理员'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditAdminModal({ admin, onClose, onSaved, roles }: { admin: AdminUser; onClose: () => void; onSaved: () => void; roles: AdminRole[] }) {
  const [form, setForm] = useState({ username: admin.username, password: '', display_name: admin.display_name || '', role: admin.role });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.username) { setErr('用户名不能为空'); return; }
    setSaving(true);
    setErr('');
    const payload: Record<string, string> = {
      username: form.username,
      display_name: form.display_name || form.username,
      role: form.role,
      updated_at: new Date().toISOString(),
    };
    if (form.password) {
      payload.password_hash = await sha256(form.password);
    }
    const { error } = await supabase.from('cms_admins').update(payload).eq('id', admin.id);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-[#1a3a5c]">编辑管理员</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">用户名 *</label>
            <input type="text" value={form.username} onChange={set('username')} placeholder="登录用户名" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">新密码 <span className="text-gray-400 font-normal">（留空则不修改）</span></label>
            <input type="text" value={form.password} onChange={set('password')} placeholder="输入新密码" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">显示名称</label>
            <input type="text" value={form.display_name} onChange={set('display_name')} placeholder="管理员显示名称" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">角色 {admin.username === 'admin' && <span className="text-gray-400 font-normal">（超级管理员不可修改角色）</span>}</label>
            <select
              value={form.role}
              onChange={set('role')}
              disabled={admin.username === 'admin'}
              className={`w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] bg-white ${admin.username === 'admin' ? 'cursor-not-allowed bg-gray-50 text-gray-400' : 'cursor-pointer'}`}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.name}>{r.display_name}</option>
              ))}
            </select>
          </div>
          {err && <p className="text-red-500 text-xs">{err}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer hover:border-gray-300 whitespace-nowrap">取消</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#1a3a5c] hover:bg-[#0d2137] text-white font-semibold py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? '保存中...' : '保存修改'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleModal({ role, roles, onClose, onSaved }: { role: AdminRole | null; roles: AdminRole[]; onClose: () => void; onSaved: () => void }) {
  const isNew = !role;
  const permModules = [
    { key: 'dashboard', label: '控制台' },
    { key: 'customers', label: '客户管理' },
    { key: 'articles', label: '文章管理' },
    { key: 'cases', label: '案例管理' },
    { key: 'faqs', label: 'FAQ管理' },
    { key: 'settings', label: '系统设置' },
  ];
  const [name, setName] = useState(role?.name || '');
  const [displayName, setDisplayName] = useState(role?.display_name || '');
  const [permissions, setPermissions] = useState<string[]>(role?.permissions || []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const togglePerm = (key: string) => {
    setPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) { setErr('角色标识不能为空'); return; }
    if (!displayName.trim()) { setErr('显示名称不能为空'); return; }
    if (permissions.length === 0) { setErr('请至少选择一个模块权限'); return; }
    // Check name uniqueness
    const existing = roles.find((r) => r.name === name.trim() && r.id !== role?.id);
    if (existing) { setErr(`角色标识 "${name.trim()}" 已存在`); return; }
    setSaving(true);
    setErr('');

    const payload = {
      name: name.trim(),
      display_name: displayName.trim(),
      permissions,
      updated_at: new Date().toISOString(),
    };

    if (isNew) {
      const { error } = await supabase.from('admin_roles').insert(payload);
      if (error) { setErr(error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('admin_roles').update(payload).eq('id', role!.id);
      if (error) { setErr(error.message); setSaving(false); return; }
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <h2 className="font-bold text-[#1a3a5c]">{isNew ? '添加角色' : '编辑角色权限'}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">角色标识 *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="如 editor"
                disabled={!isNew}
                className={`w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] ${!isNew ? 'bg-gray-50 text-gray-400' : ''}`}
              />
              {!isNew && <p className="text-xs text-gray-400 mt-1">角色标识创建后不可修改</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">显示名称 *</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="如 编辑（有限权限）"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-3">模块权限 <span className="text-gray-400 font-normal">（至少选一个）</span></label>
            <div className="grid grid-cols-2 gap-2">
              {permModules.map((pm) => {
                const checked = permissions.includes(pm.key);
                return (
                  <label
                    key={pm.key}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                      checked ? 'border-[#c9a96e] bg-[#c9a96e]/5' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePerm(pm.key)}
                      className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                    />
                    <span className={`text-sm ${checked ? 'text-[#1a3a5c] font-medium' : 'text-gray-500'}`}>
                      {pm.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {err && <p className="text-red-500 text-xs">{err}</p>}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer hover:border-gray-300 whitespace-nowrap">取消</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? '保存中...' : '保存角色'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [settings, setSettings] = useState<SettingsMap>({});
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; danger?: boolean; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const fetchAll = async () => {
    setLoading(true);
    const [sRes, aRes, bRes, rRes] = await Promise.all([
      supabase.from('site_settings').select('key, value'),
      supabase.from('cms_admins').select('id, username, role, display_name, created_at').order('created_at'),
      supabase.from('banners').select('*').order('sort_order'),
      supabase.from('admin_roles').select('*').order('id'),
    ]);
    const map: SettingsMap = {};
    (sRes.data ?? []).forEach((s) => { map[s.key] = s.value || ''; });
    setSettings(map);
    setAdmins(aRes.data ?? []);
    setBanners(bRes.data ?? []);
    setRoles(rRes.data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const setSetting = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSettings((p) => ({ ...p, [key]: e.target.value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    const seoKeys = Object.keys(settings).filter((k) => k.startsWith('seo_'));
    const upserts = seoKeys.map((key) => ({
      key,
      value: settings[key],
      updated_at: new Date().toISOString(),
    }));
    await supabase.from('site_settings').upsert(upserts, { onConflict: 'key' });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleDeleteBanner = async (id: number) => {
    setConfirmModal({
      open: true,
      title: '删除轮播图',
      message: '确认删除该轮播图？删除后不可恢复。',
      danger: true,
      onConfirm: async () => {
        await supabase.from('banners').delete().eq('id', id);
        setConfirmModal((p) => ({ ...p, open: false }));
        fetchAll();
      },
    });
  };

  const handleDeleteAdmin = async (adminUser: AdminUser) => {
    setConfirmModal({
      open: true,
      title: '删除管理员',
      message: `确认删除管理员账号「${adminUser.display_name || adminUser.username}」？此操作不可撤销。`,
      danger: true,
      onConfirm: async () => {
        await supabase.from('cms_admins').delete().eq('id', adminUser.id);
        setConfirmModal((p) => ({ ...p, open: false }));
        fetchAll();
      },
    });
  };

  const handleDeleteRole = async (role: AdminRole) => {
    const { data: linkedAdmins } = await supabase.from('cms_admins').select('id').eq('role', role.name);
    const count = (linkedAdmins ?? []).length;
    if (count > 0) {
      alert(`无法删除角色「${role.display_name}」：仍有 ${count} 个管理员账号在使用该角色。请先将这些管理员分配到其他角色后再删除。`);
      return;
    }
    setConfirmModal({
      open: true,
      title: '删除角色',
      message: `确认删除角色「${role.display_name}」？此操作不可撤销。`,
      danger: true,
      onConfirm: async () => {
        await supabase.from('admin_roles').delete().eq('id', role.id);
        setConfirmModal((p) => ({ ...p, open: false }));
        fetchAll();
      },
    });
  };

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'general', label: '站点信息', icon: 'ri-settings-3-line' },
    { key: 'seo', label: 'SEO 设置', icon: 'ri-global-line' },
    { key: 'banners', label: '轮播图管理', icon: 'ri-image-line' },
    { key: 'roles', label: '角色管理', icon: 'ri-user-settings-line' },
    { key: 'admins', label: '账号管理', icon: 'ri-admin-line' },
  ];

  if (loading) {
    return <div className="p-8 text-center text-gray-400"><i className="ri-loader-4-line text-2xl animate-spin block mb-2"></i>加载中...</div>;
  }

  const langConfig = [
    { lang: 'zh', label: '中文 (zh)', flag: '🇨🇳' },
    { lang: 'en', label: '英文 (en)', flag: '🇺🇸' },
    { lang: 'ru', label: '俄语 (ru)', flag: '🇷🇺' },
  ] as const;

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1a3a5c]">系统设置</h1>
        <p className="text-gray-400 text-sm mt-1">管理站点 Logo/二维码、SEO 信息、轮播图、角色权限与管理员账号</p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium cursor-pointer whitespace-nowrap border-b-2 transition-colors ${
              activeTab === t.key
                ? 'border-[#c9a96e] text-[#c9a96e]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${t.icon} text-base`}></i>
            {t.label}
          </button>
        ))}
      </div>

      {/* General site info */}
      {activeTab === 'general' && (
        <div className="max-w-2xl space-y-6">
          <div className="flex items-start gap-3 bg-[#c9a96e]/8 border border-[#c9a96e]/20 rounded-xl px-4 py-3">
            <i className="ri-information-line text-[#c9a96e] text-base mt-0.5"></i>
            <p className="text-[#b8944f] text-xs leading-relaxed">
              在这里修改全站 Logo、联系方式二维码和联系信息。保存后刷新页面即可在前端导航栏、页脚、联系浮窗等处生效。建议 Logo 为 PNG 透明背景，二维码图片建议 300×300px 以上。
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 space-y-5">
            <h3 className="font-bold text-[#1a3a5c] text-sm flex items-center gap-2">
              <i className="ri-image-line text-[#c9a96e]"></i>
              网站 Logo
            </h3>
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <ImageUploader
                  currentUrl={settings.logo_url || ''}
                  onChange={(url) => {
                    setSettings((p) => ({ ...p, logo_url: url }));
                    setSaved(false);
                  }}
                  category="common"
                  placeholder="点击上传 Logo 图片（PNG 透明背景最佳，最大 2MB）"
                  previewHeight="h-24"
                  maxWidth={300}
                />
              </div>
              {settings.logo_url && (
                <div className="shrink-0 w-32 h-16 rounded-lg border border-gray-200 overflow-hidden bg-white flex items-center justify-center">
                  <img src={settings.logo_url} alt="Logo 预览" className="max-w-full max-h-full object-contain" />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 space-y-5">
            <h3 className="font-bold text-[#1a3a5c] text-sm flex items-center gap-2">
              <i className="ri-wechat-line text-[#c9a96e]"></i>
              微信二维码
            </h3>
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <ImageUploader
                  currentUrl={settings.wechat_qr_url || ''}
                  onChange={(url) => {
                    setSettings((p) => ({ ...p, wechat_qr_url: url }));
                    setSaved(false);
                  }}
                  category="common"
                  placeholder="点击上传微信二维码图片（建议 300×300px，最大 2MB）"
                  previewHeight="h-40"
                  maxWidth={300}
                />
              </div>
              {settings.wechat_qr_url && (
                <div className="shrink-0 w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-white">
                  <img src={settings.wechat_qr_url} alt="微信二维码预览" className="w-full h-full object-contain" />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 space-y-5">
            <h3 className="font-bold text-[#1a3a5c] text-sm flex items-center gap-2">
              <i className="ri-whatsapp-line text-[#c9a96e]"></i>
              WhatsApp 二维码
            </h3>
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <ImageUploader
                  currentUrl={settings.whatsapp_qr_url || ''}
                  onChange={(url) => {
                    setSettings((p) => ({ ...p, whatsapp_qr_url: url }));
                    setSaved(false);
                  }}
                  category="common"
                  placeholder="点击上传 WhatsApp 二维码图片（建议 300×300px，最大 2MB）"
                  previewHeight="h-40"
                  maxWidth={300}
                />
              </div>
              {settings.whatsapp_qr_url && (
                <div className="shrink-0 w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-white">
                  <img src={settings.whatsapp_qr_url} alt="WhatsApp 二维码预览" className="w-full h-full object-contain" />
                </div>
              )}
            </div>
          </div>

          {/* Contact info management */}
          <div className="bg-white rounded-2xl p-6 space-y-5">
            <h3 className="font-bold text-[#1a3a5c] text-sm flex items-center gap-2">
              <i className="ri-contacts-line text-[#c9a96e]"></i>
              联系方式
            </h3>
            <p className="text-gray-400 text-xs">修改后将同步更新至网站联系页面、页脚及所有联系入口</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">联系电话</label>
                <input
                  type="text"
                  value={settings.contact_phone || '+86 16600032397'}
                  onChange={setSetting('contact_phone')}
                  placeholder="如 +86 16600032397"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">电子邮箱</label>
                <input
                  type="text"
                  value={settings.contact_email || 'ivfjason@gmail.com'}
                  onChange={setSetting('contact_email')}
                  placeholder="如 ivfjason@gmail.com"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">微信号</label>
                <input
                  type="text"
                  value={settings.contact_wechat || '16600032397'}
                  onChange={setSetting('contact_wechat')}
                  placeholder="如 16600032397"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">WhatsApp 号</label>
                <input
                  type="text"
                  value={settings.contact_whatsapp || '+86 18513885468'}
                  onChange={setSetting('contact_whatsapp')}
                  placeholder="如 +86 18513885468"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">办公地址</label>
                <input
                  type="text"
                  value={settings.contact_address || '吉尔吉斯斯坦·比什凯克'}
                  onChange={setSetting('contact_address')}
                  placeholder="如 吉尔吉斯斯坦·比什凯克"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
            </div>
          </div>

          <button
            onClick={async () => {
              setSaving(true);
              const generalKeys = ['logo_url', 'wechat_qr_url', 'whatsapp_qr_url', 'contact_phone', 'contact_email', 'contact_wechat', 'contact_whatsapp', 'contact_address'];
              const upserts = generalKeys.map((key) => ({
                key,
                value: settings[key] || '',
                updated_at: new Date().toISOString(),
              }));
              const { error } = await supabase.from('site_settings').upsert(upserts, { onConflict: 'key' });
              setSaving(false);
              if (error) {
                setSaved(false);
              } else {
                setSaved(true);
                setTimeout(() => setSaved(false), 2500);
              }
            }}
            disabled={saving}
            className="flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 transition-all"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>保存中...</>
            ) : saved ? (
              <><i className="ri-check-line"></i>已保存！前端已同步</>
            ) : (
              <><i className="ri-save-line"></i>保存站点信息</>
            )}
          </button>
        </div>
      )}

      {/* SEO settings */}
      {activeTab === 'seo' && (
        <div className="space-y-5 max-w-2xl">
          {/* Info banner */}
          <div className="flex items-start gap-3 bg-[#c9a96e]/8 border border-[#c9a96e]/20 rounded-xl px-4 py-3">
            <i className="ri-information-line text-[#c9a96e] text-base mt-0.5"></i>
            <p className="text-[#b8944f] text-xs leading-relaxed">
              SEO 配置会自动同步至前端首页的网页标题、描述和关键词。保存后刷新首页即可生效，有助于提升搜索引擎排名。
            </p>
          </div>

          {langConfig.map(({ lang, label }) => (
            <div key={lang} className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-[#1a3a5c] text-sm mb-4 flex items-center gap-2">
                <i className="ri-global-line text-[#c9a96e]"></i>
                {label} SEO配置
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    页面标题 <span className="text-gray-400 ml-1">建议 50-60 字符</span>
                  </label>
                  <input
                    type="text"
                    value={settings[`seo_title_${lang}`] || ''}
                    onChange={setSetting(`seo_title_${lang}`)}
                    placeholder={lang === 'zh' ? '例：吉尔吉斯斯坦试管代孕中心RTB | 合法代孕' : ''}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    已输入 {(settings[`seo_title_${lang}`] || '').length} 字符
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    页面描述 <span className="text-gray-400 ml-1">建议 120-160 字符</span>
                  </label>
                  <textarea
                    value={settings[`seo_description_${lang}`] || ''}
                    onChange={setSetting(`seo_description_${lang}`)}
                    rows={3}
                    placeholder={lang === 'zh' ? '例：RTB吉尔吉斯斯坦试管代孕中心，专业提供代孕服务、试管婴儿...' : ''}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    已输入 {(settings[`seo_description_${lang}`] || '').length} 字符
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    关键词 <span className="text-gray-400 ml-1">逗号分隔，建议 3-5 个</span>
                  </label>
                  <input
                    type="text"
                    value={settings[`seo_keywords_${lang}`] || ''}
                    onChange={setSetting(`seo_keywords_${lang}`)}
                    placeholder={lang === 'zh' ? '代孕,试管婴儿,捐卵,吉尔吉斯斯坦' : ''}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold px-8 py-3 rounded-full text-sm cursor-pointer whitespace-nowrap disabled:opacity-60 transition-all"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>保存中...</>
            ) : saved ? (
              <><i className="ri-check-line"></i>已保存！已同步至前端</>
            ) : (
              <><i className="ri-save-line"></i>保存 SEO 设置</>
            )}
          </button>
        </div>
      )}

      {/* Banner management */}
      {activeTab === 'banners' && (
        <div className="max-w-4xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-[#1a3a5c] text-base">首页轮播图管理</h2>
              <p className="text-gray-400 text-xs mt-1">上传真实横幅大图，建议尺寸 1600×900px，至少添加 1 张。修改后自动同步至首页 Hero 轮播区。</p>
            </div>
            <button
              onClick={() => { setEditingBanner(null); setShowBannerModal(true); }}
              className="flex items-center gap-1.5 bg-[#1a3a5c] text-white px-4 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap hover:bg-[#0d2137]"
            >
              <i className="ri-add-line"></i>添加轮播图
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {banners.map((b) => (
              <div key={b.id} className={`bg-white rounded-2xl overflow-hidden border-2 transition-all ${b.is_active ? 'border-transparent' : 'border-gray-200 opacity-60'}`}>
                <div className="relative w-full h-40 bg-gray-100 overflow-hidden">
                  <img src={b.img_url} alt={b.title} className="w-full h-full object-cover" />
                  {!b.is_active && (
                    <div className="absolute top-2 left-2 bg-gray-500 text-white text-xs px-2 py-0.5 rounded-full">已停用</div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-[#1a3a5c] text-sm line-clamp-1">{b.title}</h3>
                    <span className="text-xs text-gray-400">排序 {b.sort_order}</span>
                  </div>
                  {b.subtitle && <p className="text-gray-400 text-xs line-clamp-2 mb-2">{b.subtitle}</p>}
                  <div className="flex items-center gap-1 text-xs text-[#c9a96e] mb-3">
                    <i className="ri-link"></i>
                    <span>{b.link_text || b.link_url}</span>
                  </div>
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => { setEditingBanner(b); setShowBannerModal(true); }}
                      className="text-xs text-[#c9a96e] hover:underline cursor-pointer"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleDeleteBanner(b.id)}
                      className="text-xs text-gray-400 hover:text-red-500 cursor-pointer"
                    >
                      删除
                    </button>
                    <button
                      onClick={async () => {
                        await supabase.from('banners').update({ is_active: !b.is_active }).eq('id', b.id);
                        fetchAll();
                      }}
                      className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer ${b.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                    >
                      {b.is_active ? '已启用' : '已停用'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {banners.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl text-gray-400">
              <i className="ri-image-line text-4xl mb-3 block"></i>
              <p className="text-sm mb-4">暂无轮播图，点击上方按钮添加</p>
              <p className="text-xs text-gray-300">建议上传 1600×900px 的高质量横幅大图</p>
            </div>
          )}
        </div>
      )}

      {/* Role management */}
      {activeTab === 'roles' && (
        <div className="max-w-3xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-[#1a3a5c] text-base">角色权限管理</h2>
              <p className="text-gray-400 text-xs mt-1">配置各角色的模块访问权限，修改后对应管理员需重新登录生效</p>
            </div>
            <button
              onClick={() => { setEditingRole(null); setShowRoleModal(true); }}
              className="flex items-center gap-1.5 bg-[#1a3a5c] text-white px-4 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap hover:bg-[#0d2137]"
            >
              <i className="ri-add-line"></i>添加角色
            </button>
          </div>

          <div className="space-y-3">
            {roles.map((r) => {
              const permModules = [
                { key: 'dashboard', label: '控制台' },
                { key: 'customers', label: '客户管理' },
                { key: 'articles', label: '文章管理' },
                { key: 'cases', label: '案例管理' },
                { key: 'faqs', label: 'FAQ管理' },
                { key: 'settings', label: '系统设置' },
              ];
              return (
                <div key={r.id} className="bg-white rounded-2xl p-5 flex items-start gap-5">
                  <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-[#f8f5f0]">
                    <i className={`text-lg ${r.name === 'super_admin' ? 'ri-vip-crown-line text-[#c9a96e]' : 'ri-user-line text-gray-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-[#1a3a5c] text-sm">{r.display_name}</h3>
                      <span className="text-xs text-gray-400">({r.name})</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {permModules.map((pm) => {
                        const has = r.permissions?.includes(pm.key);
                        return (
                          <span
                            key={pm.key}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                              has
                                ? 'bg-[#c9a96e]/10 text-[#b8944f]'
                                : 'bg-gray-100 text-gray-400 line-through'
                            }`}
                          >
                            {pm.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 mt-1">
                    <button
                      onClick={() => { setEditingRole(r); setShowRoleModal(true); }}
                      className="text-xs text-[#c9a96e] hover:underline cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line mr-1"></i>编辑权限
                    </button>
                    <button
                      onClick={() => handleDeleteRole(r)}
                      className="text-xs text-gray-400 hover:text-red-500 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>删除
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {roles.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl text-gray-400">
              <i className="ri-user-settings-line text-4xl mb-3 block"></i>
              <p className="text-sm">暂无角色数据</p>
            </div>
          )}
        </div>
      )}

      {/* Admin management */}
      {activeTab === 'admins' && (
        <div className="max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-[#1a3a5c] text-base">账号管理</h2>
            <button onClick={() => setShowAddAdmin(true)} className="flex items-center gap-1.5 bg-[#1a3a5c] text-white px-4 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap hover:bg-[#0d2137]">
              <i className="ri-add-line"></i>添加管理员
            </button>
          </div>
          <div className="bg-white rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#f8f5f0]">
                <tr className="text-gray-500 text-xs">
                  <th className="text-left px-5 py-3.5 font-medium">账号</th>
                  <th className="text-left px-5 py-3.5 font-medium">显示名称</th>
                  <th className="text-left px-5 py-3.5 font-medium">角色</th>
                  <th className="text-left px-5 py-3.5 font-medium">创建时间</th>
                  <th className="text-left px-5 py-3.5 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {admins.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-medium text-[#1a3a5c]">{a.username}</td>
                    <td className="px-5 py-4 text-gray-500">{a.display_name || '—'}</td>
                    <td className="px-5 py-4">
                      {(() => {
                        const roleInfo = roles.find((r) => r.name === a.role);
                        const displayName = roleInfo?.display_name || a.role;
                        return (
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${a.role === 'super_admin' ? 'bg-[#c9a96e]/10 text-[#b8944f]' : 'bg-gray-100 text-gray-500'}`}>
                            {displayName}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-4 text-gray-400 text-xs">{new Date(a.created_at).toLocaleDateString('zh-CN')}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setEditingAdmin(a)}
                          className="text-xs text-[#c9a96e] hover:underline cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line mr-1"></i>编辑
                        </button>
                        {a.username !== 'admin' && (
                          <button
                            onClick={() => handleDeleteAdmin(a)}
                            className="text-xs text-gray-400 hover:text-red-500 cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddAdmin && (
        <AddAdminModal
          onClose={() => setShowAddAdmin(false)}
          onSaved={() => { setShowAddAdmin(false); fetchAll(); }}
          roles={roles}
        />
      )}

      {editingAdmin && (
        <EditAdminModal
          admin={editingAdmin}
          onClose={() => setEditingAdmin(null)}
          onSaved={() => { setEditingAdmin(null); fetchAll(); }}
          roles={roles}
        />
      )}

      {showBannerModal && (
        <BannerModal
          banner={editingBanner}
          onClose={() => setShowBannerModal(false)}
          onSaved={() => { setShowBannerModal(false); fetchAll(); }}
        />
      )}

      {showRoleModal && (
        <RoleModal
          role={editingRole}
          roles={roles}
          onClose={() => { setShowRoleModal(false); setEditingRole(null); }}
          onSaved={() => { setShowRoleModal(false); setEditingRole(null); fetchAll(); }}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((p) => ({ ...p, open: false }))}
      />
    </div>
  );
}